import Loader from "@/javascript/components/loader"
import { slideTransition } from "@/javascript/components/animations/slide"
import resized from "@/javascript/components/events/resized"
import { createLogger, nodeToString } from "@/javascript/utils/logging";

// Constants
const CSS_CLASS_OPEN = 'open';
const EVENT_ELEMENT_TOGGLED = 'elementToggled';
const ANIMATION_DEFAULT_DURATION = 200;

// Toggling System - Adds/removes 'open' class on trigger and target elements
//
// BASIC USAGE:
// <button data-toggle=".advanced-options">Show Advanced Options</button>
// <div class="advanced-options">Additional form fields...</div>
//
// MULTIPLE TARGETS:
// Same options: <button data-toggle=".details, .actions" data-toggle-always="close">
// Different options: <button data-toggles='[{"target": ".details", "always": "open"}, {"target": ".summary", "always": "close"}]'>
//
// OPTIONS:
// always: 'open'|'close' - Force specific state regardless of current state
// animation: number|'false' - Duration in ms (default: 200, 'false' disables)
// pureCss: 'true' - Use CSS transitions instead of JavaScript animations
// scrollTarget: selector - Element to scroll to after toggle completes
// hiddenField: string - ID of hidden field to update with 'true'/'false' (data-toggles only)
// data-toggle-within-closest: selector - Limit target search to closest ancestor matching this selector
//
// FORM ELEMENT BEHAVIORS:
// Button/Link: Manual toggle on click
// Radio: Accordion - one option visible per name group
// Checkbox: On/off toggle based on checked state
// Select/Selectize: Show content for selected option value (accordion pattern)
//
// All form elements respect the 'always' option to override normal behavior.
//
// EXAMPLES BY ELEMENT TYPE:
//
// Buttons:
// <button data-toggle=".sidebar" data-toggle-animation="500">Toggle Sidebar</button>
//
// Radio (accordion pattern):
// <input type="radio" name="type" value="supplier" data-toggle=".supplier-form">
// <input type="radio" name="type" value="client" data-toggle=".client-form">
//
// Checkbox (simple on/off):
// <input type="checkbox" data-toggle=".optional-fields">Show optional fields</input>
//
// Select (accordion pattern):
// <select><option value="tech" data-toggle=".tech-fields">Technology</option></select>
//
// Scoped toggle (limits search to closest ancestor):
// <button data-toggle=".content" data-toggle-within-closest=".card">Toggle card content only</button>
//
// Form integration:
// <button data-toggles='[{"target": ".new-supplier", "hiddenField": "connection_new_supplier"}]'>
// (Requires: <input type="hidden" id="connection_new_supplier" value="false">)
//
// CSS REQUIREMENTS:
// The toggling system only adds/removes the 'open' class.
// All visual styling and animations are handled by your CSS.
// Basic pattern: .target { display: none; } .target.open { display: block; }

export class Toggler {
  constructor (elem, container, onChange, opts = {}) {
    this.logger = createLogger('Toggler')

    this.elem = elem
    this.container = container
    this.always = opts.always
    this.opts = Object.assign({}, this._defaultOptions(), opts)
    this.onChange = onChange || function () {}
    this.hiddenField = null
    if (opts.hiddenField) {
      // TODO-LOADER-SCOPE: Hidden field may be in parent form outside content scope.
      // Consider passing element reference instead of ID, or using Stimulus value targets.
      this.hiddenField = document.getElementById(opts.hiddenField)
      if (!this.hiddenField) {
        this.logger.error(`Toggler: Hidden field with id '${opts.hiddenField}' not found`)
      }
    }
    this._bind()

    this.logger.debug(`Toggler: ${nodeToString(this.container)} created for ${nodeToString(this.elem)} with opts: ${JSON.stringify(this.opts)}`)
  }

  set (visible) {
    const currentlyOpen = this.container.classList.contains(CSS_CLASS_OPEN)
    if(visible === currentlyOpen) {
      this.logger.debug(`Toggler: ${nodeToString(this.container)} already visible (from ${nodeToString(this.container)})`)
      return
    }

    this.logger.debug(`Toggler${this.opts.pureCss ? ' (pureCSS)' : ''}: ${nodeToString(this.container)} set(${visible}) (from ${nodeToString(this.container)})`)

    if (this.opts.pureCss) {
      // For pure CSS, update classes immediately and let CSS handle animation
      this._updateClasses(visible)
      this._updateHiddenField()
      this._setupPureCssAnimationListeners()
      this._triggerCompletionIfNoAnimation()
      this.onChange(visible, null, this.elem, this.container)
    } else {
      this._runJavaScriptAnimation(visible)
    }
  }

  _updateClasses(visible) {
    // Update classes on both container and trigger element
    if(visible) {
      this.container.classList.add(CSS_CLASS_OPEN)
      this.elem.classList.add(CSS_CLASS_OPEN)
    } else {
      this.elem.classList.remove(CSS_CLASS_OPEN)
      this.container.classList.remove(CSS_CLASS_OPEN)
    }
  }

  _runJavaScriptAnimation(visible) {
    const animation = slideTransition(this.container, this._animationMilliseconds(), () => {
      // Update classes during animation so slideTransition can measure the difference
      this._updateClasses(visible)
      this._updateHiddenField()
    })
    animation.then(() => {
      this.onToggleComplete()
    })
    this.onChange(visible, animation, this.elem, this.container)
  }

  _setupPureCssAnimationListeners() {
    this._setupAnimationListener('transitionend', 'togglerTransitionEndBound')
    this._setupAnimationListener('animationend', 'togglerAnimationEndBound')
  }

  _setupAnimationListener(eventType, boundProperty) {
    if (!this.container[boundProperty]) {
      this.container.addEventListener(eventType, (event) => {
        if(event.target !== this.container) {
          return
        }
        this.onPureCssToggleComplete()
      })
      this.container[boundProperty] = true
    }
  }

  _triggerCompletionIfNoAnimation() {
    const animationDuration = window.getComputedStyle(this.container, null).getPropertyValue('animation-duration')
    const transitionDuration = window.getComputedStyle(this.container, null).getPropertyValue('transition-duration')

    // Handle empty values from getComputedStyle (common in test environments)
    const animDuration = parseFloat(animationDuration) || 0
    const transDuration = parseFloat(transitionDuration) || 0

    if(animDuration <= 0 && transDuration <= 0) {
      this.onPureCssToggleComplete()
    }
  }

  onPureCssToggleComplete () {
    resized(this.container)
    this.onToggleComplete()
  }


  _updateHiddenField() {
    if (this.hiddenField) {
      const isOpen = this.container.classList.contains(CSS_CLASS_OPEN)
      this.hiddenField.value = isOpen ? 'true' : 'false'
      this.logger.debug(`Toggler: Updated hidden field ${this.hiddenField.name} to ${this.hiddenField.value}`)
    }
  }

  _setRespectingAlways(defaultState) {
    if (this.always === 'close') {
      this.set(false)
    } else if (this.always === 'open') {
      this.set(true)
    } else {
      this.set(defaultState)
    }
  }

  onToggleComplete () {
    // TODO-LOADER-SCOPE: Scroll target intentionally queries entire page (could be anywhere in DOM).
    // This is a legitimate cross-scope use case. Consider passing element reference or using data attributes.
    const scrollTarget = document.querySelector(this.opts.scrollTarget)
    if(scrollTarget) {
      scrollTarget.scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"})
    }
    this.elem.dispatchEvent(new Event(EVENT_ELEMENT_TOGGLED, {bubbles: true, cancelable: false, element: this.elem }));
  }

  toggle () {
    // Normal toggle behavior - flip current state
    const currentlyOpen = this.container.classList.contains(CSS_CLASS_OPEN)
    this._setRespectingAlways(!currentlyOpen)
  }


  _bind () {
    this.elem.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.toggle()
    })
  }

  _defaultOptions () {
    return {
      animation: ANIMATION_DEFAULT_DURATION
    }
  }

  _animationMilliseconds () {
    return this.opts.animation === 'false' ? 0 : (this.opts.animation || ANIMATION_DEFAULT_DURATION)
  }
}

export class RadioToggler extends Toggler {
  _bind () {
    // TODO-LOADER-SCOPE: Radio button groups may span multiple content scopes or be in parent forms.
    // Need to find all radios with same name attribute, which could be outside loaded content.
    // Consider using Stimulus controller or scoping to closest form element.
    document.querySelectorAll(`input[type='radio'][name='${this.elem.name}']`)
      .forEach((radio) => {
        const toggler = this
        radio.addEventListener('change', function (e) {
          toggler.logger.debug(`RadioToggler: change event for ${nodeToString(toggler.elem)}, value: ${this.value}, toggler elem value: ${toggler.elem.value}`)

          const isSelected = this.value === toggler.elem.value
          toggler._setRespectingAlways(isSelected)
        })
      })
  }
}

export class CheckboxToggler extends Toggler {
  _bind () {
    this.elem.addEventListener('change', (e) => {
      this._setRespectingAlways(this.elem.checked)
    })
  }
}

// Toggles content when selected value is included in `values` passed in `opts`
export class SelectizeToggler extends Toggler {
  _bind () {
    this.elem.selectize.on('change', (newValue) => {
      this._setRespectingAlways(this.opts.values.includes(newValue))
    })
  }
}

// Toggles content when selected value is included in `values` passed in `opts`

export class SelectToggler extends Toggler {
  _bind () {
    this.elem.addEventListener('change', (e) => {
      const newValue = this.elem.value
      const newState = this.opts.values.includes(newValue)

      this.logger.debug(`SelectToggler: newValue: ${newValue} for ${nodeToString(this.elem)}`)
      this.logger.debug(`SelectToggler: set(${newState}) on ${nodeToString(this.container)} (from ${nodeToString(this.container)})`)

      this._setRespectingAlways(newState)
    })
  }
}

export class MultiTargetToggler {
  constructor(elem, targetConfigs, onChange) {
    this.logger = createLogger('MultiTargetToggler')
    this.elem = elem
    this.onChange = onChange || function () {}
    this.targetTogglers = []

    // Create individual togglers for each target
    targetConfigs.forEach(config => {
      const targets = this._findToggleTargets(config.target, elem)
      targets.forEach(target => {
        const options = {
          animation: config.animation,
          scrollTarget: config.scrollTarget,
          pureCss: config.pureCss === 'true' || config.pureCss === true,
          always: config.always,
          hiddenField: config.hiddenField
        }

        const toggler = new Toggler(elem, target, this.onChange, options)
        this.targetTogglers.push(toggler)
      })
    })

    this.logger.debug(`MultiTargetToggler: Created with ${this.targetTogglers.length} target togglers for ${nodeToString(this.elem)}`)
  }
  
  _findToggleTargets(selector, elem) {
    // Support scoped toggle target searches using data-toggle-within-closest
    if(elem.dataset.toggleWithinClosest) {
      const closestContainer = elem.closest(elem.dataset.toggleWithinClosest)
      if(closestContainer) {
        return [
          ...(closestContainer.matches(selector) ? [closestContainer] : []),
          ...closestContainer.querySelectorAll(selector)
        ];
      } else {
        this.logger.error(`MultiTargetToggler: data-toggle-within-closest selector '${elem.dataset.toggleWithinClosest}' not found`)
        return []
      }
    }

    // Default behavior - search entire document
    // TODO-LOADER-SCOPE: MultiTargetToggler intentionally searches entire document when no scope specified.
    // This is by design for page-level toggles. Should enforce data-toggle-within-closest for content-scoped usage.
    return [...document.querySelectorAll(selector)];
  }

  toggle() {
    this.targetTogglers.forEach(toggler => toggler.toggle())
  }
}


// Bind a toggler for select option that have `data-toggle='<css-selectors>'`
//
// This can be used to have a select option that toggles multiple elements when selected

// This is the toggler for selectize options. It is bound in selects.js
export class SelectizeOptionToggler {
  constructor (select) {
    this.select = select
    this.logger = createLogger('SelectizeOptionToggler')
  }

  bind () {
    const elemsToValues = new SelectElementsToValues
    const children = this.select.selectize.revertSettings.$children
    const options = [...children.find('option').add(children.filter('option'))] // Find options in optgroups too

    options
      .filter((option) => option.dataset.toggle)
      .forEach((option) => {
        elemsToValues.add(option.dataset.toggle, option.value)
      })

    this.logger.debug(`SelectizeOptionToggler: Built mapping for ${this.select.id}:\n\n${elemsToValues.toString()}`)

    Object.entries(elemsToValues.mapping).forEach((entry) => {
      const [elem, values] = entry
      // TODO: Should not be using document.querySelector as this causes issues with the
      // loader. It should be using the content from the loader
      // TODO-LOADER-SCOPE: Selectize option toggles need access to toggle targets that may be outside select's content scope.
      // Need to pass content parameter through or use data attributes to reference targets within scope.
      const toggledContent = document.querySelector(elem)

      new SelectizeToggler(this.select, toggledContent, null, {values: values})
    })
  }
}

// This is the toggler for non-selectize select options
class SelectOptionToggler {
  constructor (content, select) {
    this.content = content
    this.select = select
    this.logger = createLogger('SelectOptionToggler')
  }

  bind () {
    const elemsToValues = new SelectElementsToValues
    const options = Array.from(this.select.children)
    // TODO: How to get options from optgroups?

    options
      .filter((option) => option.dataset.toggle)
      .forEach((option) => {
        elemsToValues.add(option.dataset.toggle, option.value)
      })

    this.logger.debug(`SelectOptionToggler: Built mapping for ${this.select.id}:\n\n${elemsToValues.toString()}`)

    Object.entries(elemsToValues.mapping).forEach((entry) => {
      const [elem, values] = entry
      // TODO: Should not be using document.querySelector as this causes issues with the
      // loader. It should be using the content from the loader
      // TODO-LOADER-SCOPE: Select option toggles need access to toggle targets that may be outside select's content scope.
      // Should use this.content.querySelector() since content is passed to constructor, but needs refactoring.
      const toggledContent = document.querySelector(elem)

      new SelectToggler(this.select, toggledContent, null, { values: values })
    })
  }
}


// Used to flatten the mapping of CSS selectors to toggle to selected options.
//
// This is because the elements to be toggled can be on multiple select options
// in different combinations and we only want one toggler per toggled element,
// or they will fight each other
class SelectElementsToValues {
  constructor () {
    this.mapping = {}
  }

  add (selector, value) {
    selector.split(',').forEach((newSelector) => {
      newSelector = newSelector.trim()
      this.mapping[newSelector] ||= []
      this.mapping[newSelector].push(value)
    })
  }

  toString () {
    return Object.entries(this.mapping).map(([key, values]) => `${key}: ${values.join(', ')}`).join('\n')
  }
}
export class TogglerBinder {
  constructor(content) {
    this.logger = createLogger('TogglerBinding')
    this.content = content
  }

  bind() {
    this.content.querySelectorAll('[data-toggle], [data-toggles]').forEach((elem) => {
      if(!elem.toggleEnabled) {
        elem.toggleEnabled = true

        if (elem.dataset.toggles) {
          this._bindDataToggles(elem)
        } else if (elem.dataset.toggle && elem.dataset.toggle.length > 0) {
          this._bindDataToggle(elem)
        }
      }
    })  
  }

  _findToggleTargets(selector) {
    return [
      ...(this.content.matches(selector) ? [this.content] : []),
      ...this.content.querySelectorAll(selector)
    ];
  }

  _buildOptionsFromDataset(elem) {
    const options = {
      scrollTarget: elem.dataset.scrollTarget,
      pureCss: this._convertToBool(elem.dataset.togglePureCss),
      always: elem.dataset.toggleAlways
    };

    // Only set animation if explicitly provided, let constructor apply defaults
    if (elem.dataset.toggleAnimation !== undefined) {
      options.animation = elem.dataset.toggleAnimation;
    }

    return options;
  }

  _buildOptionsFromConfig(config) {
    return {
      animation: config.animation,
      scrollTarget: config.scrollTarget,
      pureCss: this._convertToBool(config.pureCss),
      always: config.always,
      hiddenField: config.hiddenField
    };
  }

  _convertToBool(value) {
    return value === 'true' || value === true;
  }

  _elementDescription(elem) {
    let description = elem.tagName.toLowerCase();
    if (elem.id) {
      description += `#${elem.id}`;
    }
    if (elem.className) {
      description += `.${elem.className.split(' ').join('.')}`;
    }
    return description;
  }

  _bindDataToggle(elem) {
    elem.dataset.toggle.split(',').forEach(toggleSelector => {
      toggleSelector = toggleSelector.trim()

      let toggledContents = this._findToggleTargets(toggleSelector);
      const options = this._buildOptionsFromDataset(elem);

      // Support scoped toggle target searches using data-toggle-within-closest
      if(elem.dataset.toggleWithinClosest) {
        const closestContainer = elem.closest(elem.dataset.toggleWithinClosest)
        if(closestContainer) {
          toggledContents = closestContainer.querySelectorAll(toggleSelector)
        } else {
          this.logger.error(`TogglerBinder: data-toggle-within-closest selector '${elem.dataset.toggleWithinClosest}' not found for element ${this._elementDescription(elem)}`)
          return // Don't create togglers if closest container not found
        }
      }

      Array.from(toggledContents).forEach(toggledContent => {
        this._createTogglerForElement(elem, toggledContent, options)
      })
    })
  }

  _bindDataToggles(elem) {
    try {
      const toggleConfigs = JSON.parse(elem.dataset.toggles)

      // Validate all configs have targets
      const validConfigs = toggleConfigs.filter(config => {
        if (!config.target) {
          this.logger.warn(`TogglerBinder: Missing target in data-toggles config: ${JSON.stringify(config)}`)
          return false
        }
        return true
      })

      if (validConfigs.length > 0) {
        // Create a single MultiTargetToggler for all targets
        elem.toggler = new MultiTargetToggler(elem, validConfigs)
        this.logger.debug(`MultiTargetToggler created for '${this._elementDescription(elem)}' with ${validConfigs.length} target configs`)
      }
    } catch (error) {
      this.logger.error(`TogglerBinder: Invalid JSON in data-toggles: ${elem.dataset.toggles}`, error)
    }
  }

  _createTogglerForElement(elem, toggledContent, options) {
    if(elem.nodeName === 'INPUT' && elem.type === 'radio') {
      elem.toggler = new RadioToggler(elem, toggledContent, null, options)

    } else if(elem.nodeName === 'INPUT' && elem.type === 'checkbox') {
      elem.toggler = new CheckboxToggler(elem, toggledContent, null, options)

    } else if(elem.nodeName === 'OPTION') {
      const select = elem.parentElement

      // Selectize togglers are bound in selects.js
      if(!select.toggler && select.classList.contains('no-js')) {
        select.toggler = new SelectOptionToggler(this.content, select)
        select.toggler.bind()
      }

    } else if(elem.nodeName !== 'OPTION') {
      elem.toggler = new Toggler(elem, toggledContent, null, options)
    }

    // Log the toggler creation (except for OPTION elements that don't create togglers)
    if(elem.toggler) {
      const togglerType = elem.toggler.constructor.name;
      this.logger.debug(`${togglerType} added to '${nodeToString(elem)}' for '${nodeToString(toggledContent)}'`);
    }
  }
}
Loader.functional((content, resolve) => {
  new TogglerBinder(content).bind()

  resolve()
})
